import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

export type ClientLog = Database['public']['Tables']['client_logs']['Row'];

const PAGE_SIZE = 50;

export function useClientLogs() {
  const [logs, setLogs] = useState<ClientLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [levelFilter, setLevelFilter] = useState<string>('all');

  const fetchLogs = useCallback(async (currentPage: number, currentLevel: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase
        .rpc('get_client_logs_with_user_info', {
          page_num: currentPage,
          page_size: PAGE_SIZE,
          level_filter: currentLevel
        });

      if (rpcError) {
        throw rpcError;
      }

      if (data) {
        // The RPC function returns a custom type, but it should be compatible with ClientLog
        const newLogs = data as ClientLog[];
        setLogs(prevLogs => (currentPage === 0 ? newLogs : [...prevLogs, ...newLogs]));
        setHasMore(newLogs.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (e) {
      setError(e as Error);
      console.error("Error fetching client logs:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const resetAndFetch = useCallback((level: string) => {
    setLogs([]);
    setPage(0);
    setHasMore(true);
    fetchLogs(0, level);
  }, [fetchLogs]);

  useEffect(() => {
    resetAndFetch(levelFilter);
  }, [levelFilter, resetAndFetch]);

  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchLogs(nextPage, levelFilter);
    }
  };
  
  const refresh = () => {
      resetAndFetch(levelFilter);
  }

  return { logs, loading, error, hasMore, levelFilter, setLevelFilter, loadMore, refresh };
}